import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { RunDetail } from '@sfpbxtestbot/contracts';
import { RunForm } from '../components/RunForm.js';
import { RunTimeline } from '../components/RunTimeline.js';
import { LogViewer } from '../components/LogViewer.js';
import { ScreenshotGallery } from '../components/Evidence.js';
import { Button, Callout, Card, PageHeader, StatusBadge } from '../components/primitives.js';
import { api } from '../lib/api.js';
import { useRunStream } from '../lib/useRunStream.js';

/**
 * Salesforce Org Setup.
 *
 * Most configuration is written through the Salesforce Metadata API, the same
 * path as viq.sftest. A few Setup screens that have no stable API (External
 * Client App, principal secrets, Call Center import) still use the browser.
 */
export function OrgSetupPage(): ReactNode {
  const [runId, setRunId] = useState<string>();
  const stream = useRunStream(runId);
  const [stopping, setStopping] = useState(false);

  const inProgress = stream.run?.outcome === 'running' || stream.run?.outcome === 'pending';

  async function stop(): Promise<void> {
    if (!runId) return;
    setStopping(true);
    try {
      await api.stopRun(runId);
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className="stack">
      <PageHeader
        title="Salesforce org setup"
        lede="Uninstall then install and configure the org, or upgrade the existing package only — nothing else is touched."
        actions={
          stream.run ? (
            <>
              <StatusBadge outcome={stream.run.outcome} />
              {inProgress ? (
                <Button variant="danger" size="small" busy={stopping} onClick={() => void stop()}>
                  Stop
                </Button>
              ) : null}
              <Link className="button button--ghost button--small" to={`/runs/${stream.run.id}`}>
                Full report
              </Link>
            </>
          ) : null
        }
      />

      {!runId ? (
        <>
          <Callout tone="info">
            <div>
              The default is a clean install: any existing RocketPhone package is removed first, then
              the org is configured. Choose <strong>Upgrade the existing package only</strong> to
              submit the new version in place and skip every configuration step.
            </div>
          </Callout>

          <RunForm
            stages={['sf-setup']}
            show={{ packageUrl: true, externalCredential: true }}
            submitLabel="Configure the org"
            onStarted={(run: RunDetail) => setRunId(run.id)}
          />

          <Card title="What the run does" subtitle="In this order. Most steps use the Salesforce API.">
            <ol className="stack stack--tight" style={{ paddingLeft: 20, margin: 0, fontSize: 14 }}>
              <li>Uninstall any RocketPhone package already installed.</li>
              <li>Install the package from your URL, for all users.</li>
              <li>Point RocketPhone Settings at this environment and record the package version.</li>
              <li>Make sure every Remote Site Setting this environment needs exists and is active.</li>
              <li>Make sure every Trusted URL exists, with all CSP directives ticked.</li>
              <li>Enable the client credentials flow on the External Client App.</li>
              <li>Set the Named Credential URL and the External Credential principal secrets.</li>
              <li>Clone the RocketPhone permission set, grant principal access, and assign it to you.</li>
              <li>Assign the Task page layouts and activate the RocketPhone record pages.</li>
              <li>Import the call centre definition and assign it to your user.</li>
              <li>Place the RocketPhone components on the Home, Contact, Account and Lead pages.</li>
            </ol>
          </Card>
        </>
      ) : (
        <>
          {stream.error ? <Callout tone="danger">{stream.error}</Callout> : null}
          {stream.run?.reason ? (
            <Callout tone={stream.run.outcome === 'failed' ? 'danger' : 'warning'}>
              {stream.run.reason}
            </Callout>
          ) : null}

          <Card
            title="Progress"
            subtitle={stream.connected ? 'Live' : 'Reconnecting to the live feed…'}
            actions={
              !inProgress ? (
                <Button size="small" onClick={() => setRunId(undefined)}>
                  Start another
                </Button>
              ) : null
            }
          >
            <RunTimeline runId={runId} steps={stream.run?.steps ?? []} />
          </Card>

          <Card title="Screenshots" subtitle="What the bot saw at each step, in order.">
            <ScreenshotGallery
              runId={runId}
              evidence={(stream.run?.steps ?? []).flatMap((step) => step.evidence)}
            />
          </Card>

          <Card title="Output">
            <LogViewer logs={stream.logs} />
          </Card>
        </>
      )}
    </div>
  );
}
